import React, { useState, useEffect } from 'react';
import { Assistant, ASSISTANT_CATEGORIES } from '../types/assistant';
import { assistantService } from '../services/assistant-service';
import { User, Tenant } from '../types/user';
import { useToast } from '../hooks/use-toast';
import { AssistantSharingModal } from './AssistantSharingModal';
import { userManagementService } from '../services/user-management-service';

interface AssistantsListProps {
  user: User;
  tenant: Tenant;
  onAssistantSelect: (assistant: Assistant) => void;
  onCreateAssistant: () => void;
  onEditAssistant: (assistant: Assistant) => void;
}

export function AssistantsList({ 
  user, 
  tenant, 
  onAssistantSelect, 
  onCreateAssistant,
  onEditAssistant 
}: AssistantsListProps) {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sharingAssistant, setSharingAssistant] = useState<Assistant | null>(null);
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const toast = useToast();

  useEffect(() => {
    loadAssistants();
    if (tenant.accountType === 'business') {
      loadTenantUsers();
    }
  }, [user.id, tenant.id]);

  const loadAssistants = async () => {
    try {
      setLoading(true);
      const data = await assistantService.getAssistantsByUser(user.id, tenant.id);
      setAssistants(data);
    } catch (error) {
      console.error('Erro ao carregar agentes:', error);
      toast.error('Erro ao carregar agentes');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantUsers = async () => {
    try {
      const users = await userManagementService.getTenantUsers(tenant.id);
      setTenantUsers(users);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleDeleteAssistant = async (assistant: Assistant) => {
    if (!confirm(`Tem certeza que deseja excluir o agente "${assistant.name}"?`)) {
      return;
    }

    try {
      await assistantService.deleteAssistant(assistant.id);
      setAssistants(prev => prev.filter(a => a.id !== assistant.id));
      toast.success('Agente excluído com sucesso');
    } catch (error) {
      console.error('Erro ao excluir agente:', error);
      toast.error('Erro ao excluir agente');
    }
  };

  const handleShareAssistant = (assistant: Assistant) => {
    console.log('🔗 Compartilhar agente:', assistant.name);
    console.log('📊 Tenant:', tenant);
    console.log('💼 É Business?', tenant.accountType === 'business');
    console.log('👥 Usuários carregados:', tenantUsers.length);
    setSharingAssistant(assistant);
  };

  const handleSaveSharing = async (visibility: 'private' | 'team' | 'shared', sharedWithUsers: string[]) => {
    if (!sharingAssistant) return;

    try {
      await assistantService.updateAssistantSharing(
        sharingAssistant.id,
        visibility,
        sharedWithUsers
      );

      // Atualizar localmente
      setAssistants(prev => prev.map(a => 
        a.id === sharingAssistant.id 
          ? { ...a, visibility, sharedWithUsers }
          : a
      ));

      toast.success('Configurações de compartilhamento salvas!');
    } catch (error) {
      console.error('Erro ao salvar compartilhamento:', error);
      throw error;
    }
  };


  const filteredAssistants = selectedCategory === 'all' 
    ? assistants 
    : assistants.filter(a => a.category === selectedCategory);

  const myAssistants = filteredAssistants.filter(a => a.createdBy === user.id);
  const publicAssistants = filteredAssistants.filter(a => a.isPublic && a.createdBy !== user.id);

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Agentes</h2>
          <button
            onClick={onCreateAssistant}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center space-x-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Criar</span>
          </button>
        </div>

        {/* Category Filter */}
        <div className="space-y-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todas as categorias</option>
            {ASSISTANT_CATEGORIES.map(category => (
              <option key={category.id} value={category.id}>
                {category.icon} {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Meus Agentes */}
        {myAssistants.length > 0 && (
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Meus Agentes</h3>
            <div className="space-y-2">
              {myAssistants.map(assistant => (
                <AssistantCard
                  key={assistant.id}
                  assistant={assistant}
                  onSelect={() => onAssistantSelect(assistant)}
                  onEdit={() => onEditAssistant(assistant)}
                  onDelete={() => handleDeleteAssistant(assistant)}
                  onShare={() => handleShareAssistant(assistant)}
                  canEdit={true}
                  isBusinessAccount={tenant.accountType === 'business'}
                />
              ))}
            </div>
          </div>
        )}

        {/* Agentes Públicos */}
        {publicAssistants.length > 0 && (
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Agentes da Empresa</h3>
            <div className="space-y-2">
              {publicAssistants.map(assistant => (
                <AssistantCard
                  key={assistant.id}
                  assistant={assistant}
                  onSelect={() => onAssistantSelect(assistant)}
                  canEdit={false}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredAssistants.length === 0 && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum agente encontrado</h3>
            <p className="text-gray-600 mb-4">
              {selectedCategory === 'all' 
                ? 'Crie seu primeiro agente personalizado'
                : 'Nenhum agente nesta categoria'
              }
            </p>
            <button
              onClick={onCreateAssistant}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Criar Agente
            </button>
          </div>
        )}
      </div>

      {/* Modal de Compartilhamento */}
      {sharingAssistant && (
        <AssistantSharingModal
          assistant={sharingAssistant}
          currentUser={user}
          tenantUsers={tenantUsers}
          isBusinessAccount={tenant.accountType === 'business'}
          onSave={handleSaveSharing}
          onClose={() => setSharingAssistant(null)}
        />
      )}
    </div>
  );
}

interface AssistantCardProps {
  assistant: Assistant;
  onSelect: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  canEdit: boolean;
  isBusinessAccount: boolean;
}

function AssistantCard({ assistant, onSelect, onEdit, onDelete, onShare, canEdit, isBusinessAccount }: AssistantCardProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  const category = ASSISTANT_CATEGORIES.find(c => c.id === assistant.category);

  return (
    <div className="group bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer relative">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg">
            {assistant.avatar || '🤖'}
          </div>
        </div>
        
        <div className="flex-1 min-w-0" onClick={onSelect}>
          <div className="flex items-center space-x-2 mb-1">
            <h4 className="font-medium text-gray-900 truncate">{assistant.name}</h4>
            {assistant.visibility && (
              <span className="flex-shrink-0">
                {assistant.visibility === 'private' && (
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
                {assistant.visibility === 'team' && (
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {assistant.visibility === 'shared' && (
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </span>
            )}
            {category && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${category.color}-100 text-${category.color}-800`}>
                {category.icon} {category.name}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 line-clamp-2 mb-2">{assistant.description}</p>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span>{assistant.aiProvider}</span>
            {assistant.usageCount !== undefined && (
              <span>{assistant.usageCount} usos</span>
            )}
            <span>{new Date(assistant.updatedAt).toLocaleDateString('pt-BR')}</span>
          </div>
        </div>

        {canEdit && (
          <div className="flex-shrink-0">
              <div className="flex items-center space-x-2">
              
              {/* Menu Dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  title="Mais opções"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                
                {showMenu && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowMenu(false)}
                    />
                    
                    {/* Menu */}
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      {onEdit && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(false);
                              onEdit();
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Editar Completo</span>
                          </button>
                          
                          {/* Botão de Compartilhamento (apenas para contas business) */}
                          {isBusinessAccount && onShare && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowMenu(false);
                                onShare();
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                              </svg>
                              <span>Compartilhar</span>
                            </button>
                          )}
                        </>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          onSelect();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span>Conversar</span>
                      </button>
                      
                      {onDelete && (
                        <>
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(false);
                              onDelete();
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Excluir</span>
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
